import { useEffect, useRef, useState } from 'react'
import { IconoBuscar, IconoCalendario } from '../../componentes/iconos'
import type {
  DiaRuta,
  FiltrosAgendamiento,
  SemanaReparto,
} from '../../hooks/useFiltrosAgendamiento'
import { useSugerenciasClientes } from './consultas'
import { DIAS, SEMANAS } from './tabla'
import { colorDeDia } from './coloresDia'

/**
 * Los filtros de la tabla: fecha de reparto, día de ruta, semana y buscador.
 *
 * Rediseño A: todo en una barra unificada. La fecha (que antes vivía suelta
 * arriba) entra aquí con icono y su nota de las 08:00. El día y la semana son
 * controles **segmentados** (pastillas pegadas), con el color de ruta en cada
 * día para leer de un vistazo. El día sigue a un clic —es el filtro de cada
 * día— pero ya no son dos filas de botones sueltos y desalineados.
 *
 * El buscador es un autocompletado: sugiere por nombre y por NIF, y elegir uno
 * deja su NIF como filtro. Escribir sin elegir también aplica el texto tal cual.
 */

/** Sin espera, cada tecla seria una peticion a un endpoint que tarda segundos. */
const ESPERA_BUSQUEDA = 300

/** El backend exige dos caracteres; con menos no se sugiere nada. */
const MINIMO_BUSQUEDA = 2

interface Props {
  filtros: FiltrosAgendamiento
  fecha: string
  onFecha: (fecha: string) => void
  onRuta: (ruta: DiaRuta | '') => void
  onSemana: (semana: SemanaReparto | '') => void
  onBuscar: (q: string) => void
}

/** Un botón dentro de un control segmentado (pastillas pegadas). */
function Segmento({
  activo,
  onPulsar,
  children,
  primero,
  ultimo,
}: {
  activo: boolean
  onPulsar: () => void
  children: React.ReactNode
  primero?: boolean
  ultimo?: boolean
}) {
  return (
    <button
      type="button"
      aria-pressed={activo}
      onClick={onPulsar}
      className={[
        'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foco focus-visible:z-10',
        primero ? 'rounded-l-lg' : '',
        ultimo ? 'rounded-r-lg' : '',
        activo
          ? 'bg-acento text-tinta-inversa'
          : 'bg-superficie text-tinta-2 hover:bg-elevado',
      ].join(' ')}
    >
      {children}
    </button>
  )
}

export function FiltrosTabla({ filtros, fecha, onFecha, onRuta, onSemana, onBuscar }: Props) {
  const [texto, setTexto] = useState(filtros.q)
  const [reposado, setReposado] = useState(filtros.q)
  const [abierto, setAbierto] = useState(false)
  const ultimoEnviado = useRef(filtros.q)

  useEffect(() => {
    if (filtros.q === ultimoEnviado.current) return
    ultimoEnviado.current = filtros.q
    setTexto(filtros.q)
    setReposado(filtros.q)
    setAbierto(false)
  }, [filtros.q])

  useEffect(() => {
    const temporizador = setTimeout(() => setReposado(texto), ESPERA_BUSQUEDA)
    return () => clearTimeout(temporizador)
  }, [texto])

  const sugerencias = useSugerenciasClientes(reposado, filtros.ruta, filtros.semana, abierto)
  const encontrados = sugerencias.data?.items ?? []
  const desplegado = abierto && reposado.trim().length >= MINIMO_BUSQUEDA

  const aplicar = (valor: string) => {
    ultimoEnviado.current = valor
    setTexto(valor)
    setReposado(valor)
    setAbierto(false)
    onBuscar(valor)
  }

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-borde bg-superficie p-4">
      {/* Fila 1: fecha + día + semana, alineados por su base. */}
      <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
        {/* Fecha de reparto */}
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-tinta-apagada">
            Fecha de reparto
          </span>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-borde-fuerte bg-elevado px-3 py-2 transition-colors focus-within:border-foco focus-within:ring-2 focus-within:ring-foco-halo hover:border-acento">
            <IconoCalendario aria-hidden="true" className="size-4 shrink-0 text-acento-tinta" />
            <input
              type="date"
              value={fecha}
              onChange={(evento) => onFecha(evento.target.value)}
              aria-label="Fecha de reparto"
              className="bg-transparent text-sm text-tinta outline-none [color-scheme:dark]"
            />
          </label>
          {/* Las 08:00 no son decorativas: el cron de las 06:00 filtra por esa hora. */}
          <span className="text-xs text-tinta-apagada">Se crea a las 08:00 de ese día.</span>
        </div>

        {/* Separador vertical, solo en pantallas anchas. */}
        <div className="hidden self-stretch border-l border-borde sm:block" aria-hidden="true" />

        {/* Día de ruta — control segmentado con color por día. */}
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-tinta-apagada">
            Día de ruta
          </span>
          <div
            role="group"
            aria-label="Día de ruta"
            className="inline-flex divide-x divide-borde overflow-hidden rounded-lg border border-borde-fuerte"
          >
            <Segmento activo={filtros.ruta === ''} onPulsar={() => onRuta('')} primero>
              Todos
            </Segmento>
            {DIAS.map((dia, i) => (
              <Segmento
                key={dia.clave}
                activo={filtros.ruta === dia.clave}
                onPulsar={() => onRuta(dia.clave)}
                ultimo={i === DIAS.length - 1}
              >
                <span
                  className={`size-2 rounded-full ${colorDeDia(dia.clave).fondo}`}
                  aria-hidden="true"
                />
                {dia.corta}
              </Segmento>
            ))}
          </div>
        </div>

        {/* Semana — control segmentado. */}
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-tinta-apagada">
            Semana
          </span>
          <div
            role="group"
            aria-label="Semana"
            className="inline-flex divide-x divide-borde overflow-hidden rounded-lg border border-borde-fuerte"
          >
            <Segmento activo={filtros.semana === ''} onPulsar={() => onSemana('')} primero>
              Todas
            </Segmento>
            {SEMANAS.map((semana, i) => (
              <Segmento
                key={semana.clave}
                activo={filtros.semana === semana.clave}
                onPulsar={() => onSemana(semana.clave)}
                ultimo={i === SEMANAS.length - 1}
              >
                {semana.clave.toUpperCase()}
              </Segmento>
            ))}
          </div>
        </div>
      </div>

      {/* Fila 2: buscador, a lo ancho. */}
      <div className="relative max-w-lg">
        <form
          role="search"
          onSubmit={(evento) => {
            evento.preventDefault()
            aplicar(texto)
          }}
        >
          <div className="relative">
            <IconoBuscar
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-tinta-apagada"
            />
            <input
              id="buscador-tabla"
              type="search"
              autoComplete="off"
              placeholder="Buscar cliente por nombre o NIF"
              aria-label="Buscar cliente"
              value={texto}
              onChange={(evento) => {
                setTexto(evento.target.value)
                setAbierto(true)
              }}
              onKeyDown={(evento) => {
                if (evento.key === 'Escape') setAbierto(false)
              }}
              className="w-full rounded-lg border border-borde-fuerte bg-elevado px-3 py-2 pl-9 text-sm text-tinta outline-none focus:border-foco focus:ring-2 focus:ring-foco-halo"
            />
          </div>
        </form>

        {desplegado ? (
          (sugerencias.isPending || sugerencias.isFetching) && encontrados.length === 0 ? (
            <p
              role="status"
              aria-live="polite"
              className="absolute z-10 mt-1 w-full rounded-lg border border-borde bg-superficie px-3 py-2 text-sm text-tinta-apagada shadow-lg"
            >
              Buscando clientes
            </p>
          ) : (
            <ul
              aria-label="Sugerencias de clientes"
              className="absolute z-10 mt-1 flex w-full flex-col overflow-hidden rounded-lg border border-borde bg-superficie shadow-lg"
            >
              {encontrados.map((fila) => (
                <li key={fila.cliente.contact_id}>
                  <button
                    type="button"
                    onClick={() => aplicar(fila.cliente.code || fila.cliente.nombre)}
                    className="flex w-full flex-wrap items-baseline gap-2 px-3 py-2 text-left transition-colors hover:bg-elevado"
                  >
                    <span className="text-sm font-medium text-tinta">{fila.cliente.nombre}</span>
                    <span className="text-xs text-tinta-apagada">{fila.cliente.code}</span>
                    <span className="text-xs text-tinta-apagada">{fila.cliente.ciudad}</span>
                  </button>
                </li>
              ))}
              {encontrados.length === 0 ? (
                <li className="px-3 py-2 text-sm text-tinta-apagada">Ningun cliente coincide</li>
              ) : null}
              <li>
                <button
                  type="button"
                  onClick={() => aplicar(texto)}
                  className="w-full border-t border-borde-suave px-3 py-2 text-left text-sm text-tinta-2 transition-colors hover:bg-elevado"
                >
                  Buscar {texto} en toda la tabla
                </button>
              </li>
            </ul>
          )
        ) : null}
      </div>
    </div>
  )
}
